"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { FormField, TextInput } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";
import { apiFetch, ApiError, errorMessage } from "@/lib/api-client";
import { requestAccount, signMessageWithWallet, hasInjectedWallet } from "@/components/wallet/useWallet";
import { SnsAuthButtons } from "@/components/auth/SnsAuthButtons";

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [walletBusy, setWalletBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiFetch("/api/auth/login", { method: "POST", json: { email, password } });
      router.push("/dashboard");
      router.refresh();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? "이메일 또는 비밀번호가 올바르지 않습니다."
          : errorMessage(err, "로그인에 실패했습니다."),
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function onWalletLogin() {
    setError(null);
    setWalletBusy(true);
    try {
      const address = await requestAccount();
      const { nonce, message } = await apiFetch<{ nonce: string; message: string }>("/api/wallet/nonce", {
        method: "POST",
        json: { address, purpose: "LOGIN", chainId: Number(process.env.NEXT_PUBLIC_CHAIN_ID ?? 31337) },
      });
      const signature = await signMessageWithWallet(address, message);
      await apiFetch("/api/auth/wallet-login", { method: "POST", json: { address, nonce, signature } });
      router.push("/dashboard");
      router.refresh();
    } catch (err) {
      setError(err instanceof ApiError ? walletErrorMessage(err) : err instanceof Error ? err.message : "지갑 로그인에 실패했습니다.");
    } finally {
      setWalletBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-md px-6 py-16">
      <h1 className="font-display text-3xl text-ink">로그인</h1>
      <p className="mt-2 text-sm text-body">이메일 또는 연결된 지갑으로 로그인하세요.</p>

      <Card variant="plain" className="mt-8">
        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          {error && <Alert tone="error">{error}</Alert>}
          <FormField label="이메일" htmlFor="email" required>
            <TextInput id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </FormField>
          <FormField label="비밀번호" htmlFor="password" required>
            <TextInput
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </FormField>
          <Button type="submit" disabled={submitting}>
            {submitting ? "로그인중..." : "이메일로 로그인"}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-muted-soft">
          <span className="h-px flex-1 bg-hairline" />
          또는
          <span className="h-px flex-1 bg-hairline" />
        </div>

        <Button
          type="button"
          variant="secondary"
          className="w-full"
          onClick={onWalletLogin}
          disabled={walletBusy || !hasInjectedWallet()}
        >
          {walletBusy ? "지갑 확인중..." : hasInjectedWallet() ? "지갑으로 로그인" : "지갑 확장 프로그램 없음"}
        </Button>
        <p className="mt-2 text-xs text-muted-soft">계정에 미리 연결된 지갑만 로그인에 사용할 수 있습니다.</p>

        <div className="mt-4">
          <SnsAuthButtons />
        </div>
      </Card>

      <p className="mt-6 text-center text-sm text-muted">
        계정이 없으신가요?{" "}
        <Link href="/signup" className="text-primary hover:underline">
          회원가입
        </Link>
      </p>
    </div>
  );
}

function walletErrorMessage(err: ApiError): string {
  if (err.status === 404) return "연결된 지갑이 없습니다. 먼저 이메일로 로그인해 계정에서 지갑을 연결해주세요.";
  return "지갑 서명 확인에 실패했습니다.";
}
